import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { brandCategories, allProducts } from '@/mocks/products';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentCat = brandCategories.find((c) => c.slug === slug);
  const displayProducts = slug ? (currentCat?.products || []) : allProducts;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const pageTitle = currentCat ? currentCat.name : '全部产品';
  const pageDescription = currentCat ? currentCat.description : '探索幼水若若全系列产品，找到属于你的那一份温柔。';

  return (
    <Layout>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${pageTitle} - 幼水若若`,
            description: pageDescription,
            url: `https://ruoruworld.github.io/category/${slug || ''}`,
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://ruoruworld.github.io/' },
              { '@type': 'ListItem', position: 2, name: pageTitle },
            ],
          }),
        }}
      />

      <div className="pt-24 pb-20 md:pt-28 md:pb-28 px-6 md:px-10">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb items={[{ label: '首页', path: '/' }, { label: pageTitle }]} />

          <div className="mt-8">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 tracking-wide">
              {pageTitle}
            </h1>
            <p className="mt-2 text-sm text-foreground-500 max-w-xl">
              {pageDescription}
            </p>
          </div>

          {/* Category Tabs */}
          <div className="mt-8 flex items-center gap-2 overflow-x-auto pb-2">
            <Link
              to="/category/ruoruo-series"
              className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all duration-200 ${
                !slug
                  ? 'bg-primary-500 text-background-50'
                  : slug === 'ruoruo-series'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              若若系列
            </Link>
            <Link
              to="/category/youshui-series"
              className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all duration-200 ${
                slug === 'youshui-series'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              幼水系列
            </Link>
            <Link
              to="/category/limited-collection"
              className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all duration-200 ${
                slug === 'limited-collection'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              限定系列
            </Link>
          </div>

          {/* Product Grid */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {displayProducts.map((product) => (
              <div
                key={product.id}
                className="group rounded-lg bg-background-100 overflow-hidden hover:bg-background-200/50 transition-all duration-300"
              >
                <div className="w-full aspect-square overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <h3 className="text-base font-heading text-foreground-800 group-hover:text-primary-500 transition-colors">
                    {product.name}
                  </h3>
                  <p className="mt-1 text-xs text-foreground-400 italic">
                    {product.tagline}
                  </p>
                  <p className="mt-2 text-sm text-foreground-500 leading-relaxed line-clamp-2">
                    {product.description}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {product.features.map((feat, idx) => (
                      <span
                        key={idx}
                        className="px-2.5 py-1 rounded-full bg-secondary-100/80 text-secondary-700 text-xs"
                      >
                        {feat}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {displayProducts.length === 0 && (
            <div className="mt-14 text-center py-16">
              <p className="text-foreground-400">暂无产品</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}